import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { cn } from '../../lib/utils';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Activity,
  TrendingUp,
  TrendingDown,
  Clock,
} from 'lucide-react';

interface HealthStatusCardProps {
  title: string;
  status: 'HEALTHY' | 'WARNING' | 'CRITICAL';
  value?: string | number;
  subtitle?: string;
  trend?: 'up' | 'down' | 'stable';
  trendValue?: string;
  lastUpdated?: string;
  className?: string;
}

const statusConfig = {
  HEALTHY: {
    icon: CheckCircle2,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    label: 'Saudável',
  },
  WARNING: {
    icon: AlertTriangle,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    label: 'Atenção',
  },
  CRITICAL: {
    icon: XCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    label: 'Crítico',
  },
};

export function HealthStatusCard({
  title,
  status,
  value,
  subtitle,
  trend,
  trendValue,
  lastUpdated,
  className,
}: HealthStatusCardProps) {
  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <Card className={cn(config.borderColor, 'border-2', className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {title}
          </CardTitle>
          <Badge variant="outline" className={cn('gap-1', config.color)}>
            <Icon className="h-3 w-3" />
            {config.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-end justify-between">
          <div>
            {value !== undefined && (
              <p className={cn('text-2xl font-bold', config.color)}>
                {value}
              </p>
            )}
            {subtitle && (
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            )}
          </div>
          {trend && (
            <div className={cn(
              'flex items-center gap-1 text-sm',
              trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-500'
            )}>
              {trend === 'up' && <TrendingUp className="h-4 w-4" />}
              {trend === 'down' && <TrendingDown className="h-4 w-4" />}
              {trendValue && <span>{trendValue}</span>}
            </div>
          )}
        </div>
        {lastUpdated && (
          <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>Atualizado: {lastUpdated}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface HealthSummaryCardsProps {
  totalAssets: number;
  healthyAssets: number;
  warningAssets: number;
  criticalAssets: number;
  className?: string;
}

export function HealthSummaryCards({
  totalAssets,
  healthyAssets,
  warningAssets,
  criticalAssets,
  className,
}: HealthSummaryCardsProps) {
  const overallStatus = criticalAssets > 0 
    ? 'CRITICAL' 
    : warningAssets > 0 
      ? 'WARNING' 
      : 'HEALTHY';

  return (
    <div className={cn('grid gap-4 md:grid-cols-4', className)}>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Total de Ativos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-600" />
            <span className="text-2xl font-bold">{totalAssets}</span>
          </div>
        </CardContent>
      </Card>

      <Card className="border-green-200 border-2">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Saudáveis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <span className="text-2xl font-bold text-green-600">{healthyAssets}</span>
          </div>
        </CardContent>
      </Card>

      <Card className={cn(warningAssets > 0 && 'border-yellow-200 border-2')}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Atenção
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <AlertTriangle className={cn('h-5 w-5', warningAssets > 0 ? 'text-yellow-600' : 'text-gray-400')} />
            <span className={cn('text-2xl font-bold', warningAssets > 0 ? 'text-yellow-600' : 'text-gray-400')}>
              {warningAssets}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card className={cn(criticalAssets > 0 && 'border-red-200 border-2')}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Críticos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <XCircle className={cn('h-5 w-5', criticalAssets > 0 ? 'text-red-600' : 'text-gray-400')} />
            <span className={cn('text-2xl font-bold', criticalAssets > 0 ? 'text-red-600' : 'text-gray-400')}>
              {criticalAssets}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default HealthStatusCard;
